import { ZoneDimensions } from "../../../types";
import { Cell } from "./types";

const cellBigEnough = (threshold: number) => (cell: ZoneDimensions): boolean => {
  return cell.maxY - cell.minY >= threshold && cell.maxX - cell.minX >= threshold;
};

const sortNumbers = (a: number, b: number): number => a - b;

const cellWithin = <T extends ZoneDimensions | Cell>(a: T, b: T): boolean =>
  a.minX >= b.minX && a.maxX <= b.maxX && a.minY >= b.minY && a.maxY <= b.maxY;

export const snapCells = <T extends ZoneDimensions | Cell>(
  cells: Array<T>,
  zoneWidth: number,
  zoneHeight: number,
  threshold: number,
): Array<T> => {
  const uniqueCols: { [col: string]: true } = {};
  const uniqueRows: { [col: string]: true } = {};
  for (const cell of cells) {
    uniqueCols[cell.minX.toString()] = true;
    uniqueCols[cell.maxX.toString()] = true;
    uniqueRows[cell.minY.toString()] = true;
    uniqueRows[cell.maxY.toString()] = true;
  }
  const columns = Object.keys(uniqueCols)
    .map(parseFloat)
    .sort(sortNumbers);
  const rows = Object.keys(uniqueRows)
    .map(parseFloat)
    .sort(sortNumbers);
  const mergeCols: { [oldValue: string]: number } = {};
  const mergeRows: { [oldValue: string]: number } = {};
  for (let i = 0; i < columns.length - 1; i++) {
    if (columns[i + 1] - columns[i] > threshold) {
      continue;
    }
    let mergedValue = (columns[i] + columns[i + 1]) / 2;
    if (columns[i] === 0) {
      mergedValue = 0;
    } else if (columns[i + 1] === zoneWidth) {
      mergedValue = zoneWidth;
    }
    mergeCols[columns[i].toString()] = mergedValue;
    mergeCols[columns[i + 1].toString()] = mergedValue;
    i++;
  }
  for (let i = 0; i < rows.length - 1; i++) {
    if (rows[i + 1] - rows[i] > threshold) {
      continue;
    }
    let mergedValue = (rows[i] + rows[i + 1]) / 2;
    if (rows[i] === 0) {
      mergedValue = 0;
    } else if (rows[i + 1] === zoneHeight) {
      mergedValue = zoneHeight;
    }
    mergeRows[rows[i].toString()] = mergedValue;
    mergeRows[rows[i + 1].toString()] = mergedValue;
    i++;
  }
  let toReturn = cells;
  if (Object.keys(mergeCols).length > 0 || Object.keys(mergeRows).length > 0) {
    toReturn = cells.map(c => {
      const minX = c.minX.toString();
      const minY = c.minY.toString();
      const maxX = c.maxX.toString();
      const maxY = c.maxY.toString();
      if (minX in mergeCols || minY in mergeRows || maxX in mergeCols || maxY in mergeRows) {
        return {
          ...c,
          maxX: maxX in mergeCols ? mergeCols[maxX] : c.maxX,
          maxY: maxY in mergeRows ? mergeRows[maxY] : c.maxY,
          minX: minX in mergeCols ? mergeCols[minX] : c.minX,
          minY: minY in mergeRows ? mergeRows[minY] : c.minY,
        };
      }
      return c;
    });
  }
  const nonOverlap = [];
  for (const cell of toReturn) {
    if (toReturn.some(c => cell !== c && cellWithin(cell, c))) {
      continue;
    }
    nonOverlap.push(cell);
  }
  toReturn = nonOverlap;
  const filtered = toReturn.filter(cellBigEnough(threshold));
  if (filtered.length < toReturn.length) {
    return snapCells(filtered, zoneWidth, zoneHeight, threshold);
  }
  return toReturn;
};
